import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Container } from "../common/Spacing";
import {
  getBestTopNewArrivalTabProducts,
  fetchSystemData,
} from "../services/apiCalls";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { CurrencyContext } from "../context/CurrencyContext";
import TabCard from "./tabCard";

const TabSection = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [products, setProducts] = useState({
    BestSellers: [],
    NewArrivals: [],
    TopSellings: [],
  });
  const navigate = useNavigate();
  const { selectedCurrency } = useContext(CurrencyContext);

  // Fetch system settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await fetchSystemData();
        setSettings(data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchSettings();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchGetProducts = async () => {
      try {
        const response = await getBestTopNewArrivalTabProducts();
        const {
          best = [],
          new_arrives = [],
          top_selling = [],
        } = response?.data || {};

        setProducts({
          BestSellers: best || [],
          NewArrivals: new_arrives || [],
          TopSellings: top_selling || [],
        });
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchGetProducts();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCardClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Dynamically determine visible tabs
  const visibleTabs = [];
  if (settings?.is_show_best_sales === 1) {
    visibleTabs.push("BestSellers");
  }
  if (settings?.is_show_new_arrivals === 1) {
    visibleTabs.push("NewArrivals");
  }
  if (settings?.is_show_top_selling === 1) {
    visibleTabs.push("TopSellings");
  }

  return (
    <Container>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            width: "8px",
            height: "8px",
            backgroundColor: "#9E6747",
            transform: "rotate(45deg)",
          }}
        />
        <Typography
          variant="p"
          fontSize={"12px"}
          color={"#9E6747"}
          sx={{
            fontFamily: "'Nunito Sans', sans-serif",
            textTransform: "uppercase",
            letterSpacing: "2px",
          }}
        >
          Discover First
        </Typography>
      </Box>
      <Box
        display={"flex"}
        sx={{
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={"600"}
          component="h2"
          width={"fit-content"}
          sx={{
            cursor: "pointer",
            transition: "color 0.3s ease",
            fontSize: { xs: "24px", sm: "32px", md: "40px" },
            "&:hover": {
              // color: "#9E6747",
            },
          }}
        >
          Be the First to Explore
        </Typography>
        <Box>
          {visibleTabs.map((tab, index) => (
            <Box
              key={index}
              role="tabpanel"
              hidden={selectedTab !== index}
              id={`tabpanel-${index}`}
              aria-labelledby={`tab-${index}`}
            >
              {selectedTab === index && (
                <Grid container spacing={4}>
                  {/* Add View All Products Button */}
                  {products[tab]?.length > 8 && (
                    <Grid
                      item
                      xs={12}
                      sx={{ display: "flex", justifyContent: "end" }}
                    >
                      <Link
                        to="/custom-products"
                        state={{
                          title: tab.replace(/([A-Z])/g, " $1").trim(),
                          products: products[tab],
                        }}
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{
                            borderRadius: "0px",
                            textTransform: "none",
                            fontWeight: "bold",
                            px: 4,
                          }}
                        >
                          View All Products
                        </Button>
                      </Link>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        centered
        textColor="primary"
        indicatorColor="primary"
        TabIndicatorProps={{ style: { display: "none" } }}
        sx={{
          borderBottom: "1px solid #1e1e1e",
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: { xs: "0.8rem", sm: "0.8rem" },
            paddingRight: "10px",
            marginRight: "10px",
            // color: "#333",
            fontFamily: "'Nunito', sans-serif",
            transition: "0.3s",
            borderRadius: 0,
            position: "relative",
            display: "inline-block",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "0%",
              height: "1px",
              backgroundColor: "#9E6747",
              transition: "width 0.3s ease",
            },
          },
          "& .MuiTab-root:hover": {
            color: "#9E6747",
            "&::after": {
              width: "100%",
            },
          },
          "& .MuiTab-root[tabIndex='0']": {
            color: "#9E6747",
            "&::after": {
              width: "100%",
            },
          },
        }}
      >
        {visibleTabs.map((tab, index) => (
          <Tab key={index} label={tab.replace(/([A-Z])/g, " $1").trim()} />
        ))}
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {visibleTabs.map((tab, index) => (
          <Box
            key={index}
            role="tabpanel"
            hidden={selectedTab !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
          >
            {selectedTab === index && (
              <Grid container spacing={4}>
                {/* Add View All Products Button */}
                {products[tab]?.length > 8 && (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "end" }}
                  >
                    <Link
                      to="/custom-products"
                      state={{
                        title: tab.replace(/([A-Z])/g, " $1").trim(),
                        products: products[tab],
                      }}
                      style={{ textDecoration: "none" }}
                    >
                      {/* <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{
                          borderRadius: "0px",
                          textTransform: "none",
                          fontWeight: "bold",
                          px: 4,
                        }}
                      >
                        View All Products
                      </Button> */}
                    </Link>
                  </Grid>
                )}

                {products[tab]?.slice(0, 8).map((product) => (
                  <Grid item xs={12} sm={6} md={3} key={product.id}>
                    <TabCard
                      key={product.id}
                      product={product}
                      selectedCurrency={selectedCurrency}
                      handleCardClick={handleCardClick}
                      hoveredProductId={hoveredProductId}
                      setHoveredProductId={setHoveredProductId}
                      isHovered={isHovered}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default TabSection;
